import { roleIcon } from './view-helpers.js';

function createRoleElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function appendTrustedRoleIcon(root, code) {
  root.append(document.createRange().createContextualFragment(roleIcon(code)));
}

function appendCustomRoleIcon(root, add = false) {
  const markup = add
    ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
    : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
  root.append(document.createRange().createContextualFragment(markup));
}

function createPublicCard(role) {
  const card = createRoleElement('button', 'aichat-role-card');
  card.type = 'button';
  card.dataset.roleCode = role.code || '';
  card.setAttribute('aria-label', `使用 ${role.name || '该角色'} 开始新对话`);
  const icon = createRoleElement('div', 'aichat-role-card-icon');
  appendTrustedRoleIcon(icon, role.code);
  card.append(
    icon,
    createRoleElement('div', 'aichat-role-card-name', role.name || '未命名角色'),
    createRoleElement('div', 'aichat-role-card-desc', role.description || ''),
    createRoleElement('div', 'aichat-role-card-meta', `${role.defaultProvider || '默认'} · ${role.defaultModel || '默认模型'}`),
  );
  return card;
}

function createCustomCard(role) {
  const card = createRoleElement('div', 'aichat-role-card aichat-role-card--custom');
  card.dataset.roleCode = role.code || '';
  const remove = createRoleElement('button', 'aichat-role-card-del', '×');
  remove.type = 'button';
  remove.dataset.deleteCustom = role.code || '';
  remove.title = '删除角色';
  const icon = createRoleElement('div', 'aichat-role-card-icon aichat-role-card-icon--custom');
  appendCustomRoleIcon(icon);
  const meta = createRoleElement('div', 'aichat-role-card-meta');
  const edit = createRoleElement('button', 'aichat-role-card-edit', '⚙');
  edit.type = 'button';
  edit.dataset.editCustom = role.code || '';
  edit.title = '编辑';
  meta.append(createRoleElement('span', '', `${role.defaultProvider || '继承全局'} · ${role.defaultModel || '默认模型'}`), edit);
  card.append(
    createRoleElement('div', 'aichat-role-card-badge', '自定义'),
    remove,
    icon,
    createRoleElement('div', 'aichat-role-card-name', role.name || '未命名角色'),
    createRoleElement('div', 'aichat-role-card-desc', role.description || '我的自定义角色'),
    meta,
  );
  return card;
}

function createCustomAddCard() {
  const card = createRoleElement('button', 'aichat-role-card aichat-role-card--add');
  card.type = 'button';
  card.dataset.action = 'create-custom';
  card.setAttribute('aria-label', '创建自定义角色');
  const icon = createRoleElement('div', 'aichat-role-card-icon aichat-role-card-icon--add');
  appendCustomRoleIcon(icon, true);
  card.append(
    icon,
    createRoleElement('div', 'aichat-role-card-name', '自定义角色'),
    createRoleElement('div', 'aichat-role-card-desc', '创建属于你自己的 AI 角色，模型自动继承系统设置'),
    createRoleElement('div', 'aichat-role-card-meta', '点击初始化'),
  );
  return card;
}

function createCardsGrid(roleList) {
  const grid = createRoleElement('div', 'aichat-role-cards-grid');
  const customRoles = roleList.filter((role) => String(role.code || '').startsWith('user_'));
  const publicRoles = roleList.filter((role) => !String(role.code || '').startsWith('user_'));
  grid.append(...publicRoles.map(createPublicCard), ...customRoles.map(createCustomCard), createCustomAddCard());
  return grid;
}

export function renderRoleCards(root, roleList = []) {
  if (!root) return;
  if (!roleList.length) {
    const state = createRoleElement('div', 'aichat-role-state');
    const grid = createCardsGrid([]);
    grid.classList.add('aichat-role-cards-grid--empty');
    state.append(
      createRoleElement('strong', '', '暂无可用角色'),
      createRoleElement('div', '', '管理员尚未配置公共角色，你仍可以创建自己的角色开始使用。'),
      grid,
    );
    root.replaceChildren(state);
    return;
  }
  const header = createRoleElement('div', 'aichat-role-cards-header');
  header.append(
    createRoleElement('h2', '', '你的亚马逊全栈智囊，已全部就绪'),
    createRoleElement('p', '', '战术挑刺、爆款选品、顶层战略，选择一个专业视角点击开聊'),
  );
  root.replaceChildren(header, createCardsGrid(roleList));
}

export function renderRoleLoading(root) {
  if (!root) return;
  const header = createRoleElement('div', 'aichat-role-cards-header');
  header.append(createRoleElement('h2', '', '正在准备你的 AI 团队'), createRoleElement('p', '', '加载角色与能力配置…'));
  const skeleton = createRoleElement('div', 'aichat-role-skeleton');
  skeleton.setAttribute('aria-label', '角色加载中');
  skeleton.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
  root.replaceChildren(header, skeleton);
}

export function renderRoleError(root, message = '角色加载失败') {
  if (!root) return;
  const state = createRoleElement('div', 'aichat-role-state');
  const retry = createRoleElement('button', 'aichat-state-action', '重新加载角色');
  retry.type = 'button';
  retry.dataset.action = 'retry-load-roles';
  state.append(
    createRoleElement('strong', '', message),
    createRoleElement('div', '', '请检查网络后重试，已存在的会话不会受到影响。'),
    retry,
  );
  root.replaceChildren(state);
}
